import { useEffect, useRef, useState } from 'preact/hooks'

type Identite = {
  nom: string
  coordonnees: string
  couleur: string
  pied_page: string
  logo_jpeg_base64?: string
}
type Clause = { id: string; titre: string; texte: string }
type Modele = {
  nom: string
  titre: string
  introduction: string
  clauses: Clause[]
  conclusion: string
  identite: Identite
}
type Resume = {
  id: string
  nom: string
  version_id: string
  numero: number
  cree_le_utc: string
  archive: boolean
}
type Version = Omit<Resume, 'nom'> & { document: Modele }
type Variable = { cle: string; libelle: string; exemple: string }
type Donnees = {
  modeles: Resume[]
  identite: Identite
  variables: Variable[]
  modele_initial: Modele
  signes: {
    id: string
    demande_id: string
    nom_tape: string
    signe_le_utc: string
  }[]
}
const date = (s: string) =>
  new Intl.DateTimeFormat('fr-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(s))
const copie = <T,>(v: T): T => structuredClone(v)
const uid = () => crypto.randomUUID()
const badge = (texte: string) => <span class="doc-badge">{texte}</span>

export function DocumentsPane({
  baseUrl,
  permissions,
  onAuth,
  visible = true,
}: {
  baseUrl: string
  permissions: string[]
  onAuth?: () => void
  visible?: boolean
}) {
  const [donnees, setDonnees] = useState<Donnees | null>(null)
  const [onglet, setOnglet] = useState<'modeles' | 'identite' | 'signes'>(
    'modeles',
  )
  const [recherche, setRecherche] = useState(''),
    [archives, setArchives] = useState(false)
  const [erreur, setErreur] = useState(''),
    [message, setMessage] = useState(''),
    [occupe, setOccupe] = useState(false)
  const [edition, setEdition] = useState<{
    id?: string
    version_id?: string
    numero?: number
    document: Modele
    initial: string
    archive?: boolean
  } | null>(null)
  const [identite, setIdentite] = useState<Identite | null>(null)
  const [apercu, setApercu] = useState<{
    html: string
    pdf_base64: string
    source: string
  } | null>(null)
  const [apercuOccupe, setApercuOccupe] = useState(false),
    [apercuErreur, setApercuErreur] = useState('')
  const [historique, setHistorique] = useState<
    { version_id: string; numero: number; cree_le_utc: string }[] | null
  >(null)
  const [versionLue, setVersionLue] = useState<string | null>(null)
  const cible = useRef<{
    element: HTMLTextAreaElement | HTMLInputElement
    champ: string
    clause?: string
  } | null>(null)
  const importLogo = useRef<HTMLInputElement>(null),
    requeteApercu = useRef(0)
  const modifier = permissions.includes('services.modifier')
  const change = Boolean(
    edition && JSON.stringify(edition.document) !== edition.initial,
  )
  const marqueChangee = Boolean(
    identite &&
    donnees &&
    JSON.stringify(identite) !== JSON.stringify(donnees.identite),
  )
  async function api(path: string, method = 'GET', body?: unknown) {
    const r = await fetch(`${baseUrl}/api/admin/documents${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (r.status === 401) onAuth?.()
    const data = await r.json()
    if (!r.ok)
      throw new Error(data.erreur || 'Cette action n’a pas abouti. Réessayez.')
    return data
  }
  async function charger() {
    const d = (await api('')) as Donnees
    setDonnees(d)
    setIdentite(copie(d.identite))
    return d
  }
  useEffect(() => {
    if (visible && !change && !marqueChangee)
      charger().catch((e) => setErreur(e.message))
  }, [baseUrl, visible])
  useEffect(() => {
    if (change || marqueChangee) setMessage('')
  }, [change, marqueChangee])
  useEffect(() => {
    const prevenir = (e: BeforeUnloadEvent) => {
      if (change || marqueChangee) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', prevenir)
    return () => window.removeEventListener('beforeunload', prevenir)
  }, [change, marqueChangee])
  async function agir(action: () => Promise<void>) {
    setOccupe(true)
    setErreur('')
    setMessage('')
    try {
      await action()
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupe(false)
    }
  }
  function quitter() {
    if (
      change &&
      !window.confirm('Quitter sans enregistrer les modifications du modèle ?')
    )
      return
    setEdition(null)
    setApercu(null)
    setHistorique(null)
    setVersionLue(null)
    setErreur('')
    setMessage('')
  }
  function ouvrir(v: Version) {
    cible.current = null
    requeteApercu.current++
    setEdition({
      id: v.id,
      version_id: v.version_id,
      numero: v.numero,
      archive: v.archive,
      document: copie(v.document),
      initial: JSON.stringify(v.document),
    })
    setHistorique(null)
    setVersionLue(null)
    setApercu(null)
    setApercuErreur('')
    setMessage('')
    setErreur('')
  }
  function nouveau() {
    if (!donnees) return
    cible.current = null
    const d = copie(donnees.modele_initial)
    setEdition({ document: d, initial: '' })
    setApercu(null)
    setHistorique(null)
    setVersionLue(null)
    setMessage('')
    setErreur('')
  }
  function patch(changement: Partial<Modele>) {
    setEdition((v) =>
      v ? { ...v, document: { ...v.document, ...changement } } : v,
    )
  }
  function patchClause(id: string, changement: Partial<Clause>) {
    if (edition)
      patch({
        clauses: edition.document.clauses.map((c) =>
          c.id === id ? { ...c, ...changement } : c,
        ),
      })
  }
  function deplacer(id: string, direction: number) {
    if (!edition) return
    const clauses = [...edition.document.clauses],
      i = clauses.findIndex((c) => c.id === id),
      j = i + direction
    if (j < 0 || j >= clauses.length) return
    ;[clauses[i], clauses[j]] = [clauses[j]!, clauses[i]!]
    patch({ clauses })
  }
  function inserer(cle: string) {
    if (!edition) return
    const c = cible.current,
      valeur = `{{${cle}}}`
    if (!c || !c.element.isConnected) {
      patch({
        conclusion: `${edition.document.conclusion}${edition.document.conclusion ? '\n' : ''}${valeur}`,
      })
      return
    }
    const el = c.element,
      debut = el.selectionStart ?? el.value.length,
      fin = el.selectionEnd ?? debut,
      nouveau = el.value.slice(0, debut) + valeur + el.value.slice(fin)
    if (c.clause) patchClause(c.clause, { [c.champ]: nouveau })
    else patch({ [c.champ]: nouveau })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(debut + valeur.length, debut + valeur.length)
    })
  }
  async function generer(
    d = edition?.document,
    id = edition?.id,
    vid = edition?.version_id,
  ) {
    if (!d) return
    const n = ++requeteApercu.current
    setApercuOccupe(true)
    setApercuErreur('')
    try {
      const p =
        modifier && !versionLue
          ? await api('/apercu', 'POST', { document: d })
          : await api(
              `/modeles/${id}/apercu${vid ? `?version_id=${encodeURIComponent(vid)}` : ''}`,
            )
      if (n === requeteApercu.current)
        setApercu({ ...p, source: JSON.stringify(d) })
    } catch (e) {
      if (n === requeteApercu.current) setApercuErreur((e as Error).message)
    } finally {
      if (n === requeteApercu.current) setApercuOccupe(false)
    }
  }
  function telecharger() {
    if (!apercu) return
    const bytes = Uint8Array.from(atob(apercu.pdf_base64), (c) =>
        c.charCodeAt(0),
      ),
      url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })),
      lien = document.createElement('a')
    lien.href = url
    lien.download = `${(edition?.document.nom || 'contrat').replace(/[^a-zA-Z0-9À-ÿ -]/g, '').slice(0, 70)}-apercu.pdf`
    lien.click()
    setTimeout(() => URL.revokeObjectURL(url), 30000)
  }
  async function sauvegarder() {
    if (!edition) return
    await agir(async () => {
      const v = (await api(
        edition.id ? `/modeles/${edition.id}` : '/modeles',
        edition.id ? 'PUT' : 'POST',
        { document: edition.document, version_id: edition.version_id },
      )) as Version
      ouvrir(v)
      await charger()
      setMessage(
        `Version ${v.numero} enregistrée. Vous pouvez la choisir dans un parcours.`,
      )
      await generer(v.document, v.id, v.version_id)
    })
  }
  async function logo(fichier?: File) {
    if (!fichier) return
    await agir(async () => {
      if (!['image/png', 'image/jpeg'].includes(fichier.type))
        throw new Error('Choisissez une image PNG ou JPEG.')
      if (fichier.size > 750000)
        throw new Error('Choisissez un logo de moins de 750 Ko.')
      const data_url = await new Promise<string>((resolve, reject) => {
        const lecteur = new FileReader()
        lecteur.onload = () => resolve(String(lecteur.result))
        lecteur.onerror = () =>
          reject(new Error('Le fichier n’a pas pu être lu.'))
        lecteur.readAsDataURL(fichier)
      })
      const r = await api('/logo', 'POST', { data_url })
      setIdentite((i) =>
        i ? { ...i, logo_jpeg_base64: r.logo_jpeg_base64 } : i,
      )
    })
    if (importLogo.current) importLogo.current.value = ''
  }
  const editPossible = modifier && !edition?.archive && !versionLue
  const champ = (
    titre: string,
    nom: 'introduction' | 'conclusion',
    valeur: string,
  ) => (
    <label>
      {titre}
      <textarea
        value={valeur}
        rows={3}
        maxLength={5000}
        disabled={!editPossible}
        onFocus={(e) => {
          cible.current = { element: e.currentTarget, champ: nom }
        }}
        onInput={(e) => patch({ [nom]: e.currentTarget.value })}
      />
    </label>
  )
  return (
    <section class="doc-pane" aria-label="Documents et contrats">
      {!edition && (
        <>
          <div class="doc-tabs" role="tablist" aria-label="Documents">
            {(['modeles', 'identite', 'signes'] as const)
              .filter(
                (t) => t !== 'signes' || permissions.includes('demandes.lire'),
              )
              .map((t) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={onglet === t}
                  onClick={() => {
                    if (
                      marqueChangee &&
                      !window.confirm(
                        'Quitter sans enregistrer l’identité visuelle ?',
                      )
                    )
                      return
                    if (donnees) setIdentite(copie(donnees.identite))
                    setOnglet(t)
                    setMessage('')
                    setErreur('')
                  }}
                >
                  {t === 'modeles'
                    ? 'Modèles de contrat'
                    : t === 'identite'
                      ? 'Identité visuelle'
                      : 'Documents signés'}
                </button>
              ))}
          </div>
        </>
      )}
      {erreur && (
        <div class="doc-alert" role="alert">
          {erreur}
          {!donnees && (
            <button
              onClick={() =>
                agir(async () => {
                  await charger()
                })
              }
            >
              Réessayer
            </button>
          )}
        </div>
      )}
      {message && (
        <div class="doc-success" role="status">
          {message}
        </div>
      )}
      {!donnees && !erreur && (
        <p class="doc-empty" role="status">
          Chargement des documents…
        </p>
      )}
      {donnees && !edition && onglet === 'modeles' && (
        <>
          <div class="doc-heading">
            <div>
              <h2>Vos contrats, prêts à réutiliser</h2>
              <p>
                Composez un modèle, puis ajoutez-le à l’étape Contrat d’un
                parcours.
              </p>
            </div>
            {modifier && (
              <button class="doc-primary" onClick={nouveau}>
                + Créer un modèle
              </button>
            )}
          </div>
          {donnees.modeles.length > 0 && (
            <div class="doc-search">
              <input
                type="search"
                aria-label="Rechercher un modèle"
                placeholder="Rechercher un modèle"
                value={recherche}
                onInput={(e) => setRecherche(e.currentTarget.value)}
              />
              <label>
                <input
                  type="checkbox"
                  checked={archives}
                  onChange={(e) => setArchives(e.currentTarget.checked)}
                />{' '}
                Voir les archives
              </label>
            </div>
          )}
          <div class="doc-modeles">
            {donnees.modeles
              .filter(
                (m) =>
                  m.archive === archives &&
                  m.nom
                    .toLocaleLowerCase()
                    .includes(recherche.toLocaleLowerCase()),
              )
              .map((m) => (
                <article class="doc-modele" key={m.id}>
                  <div class="doc-paper-icon" aria-hidden="true">
                    ≡
                  </div>
                  <div>
                    <h3>{m.nom}</h3>
                    <p>
                      Version {m.numero} · {date(m.cree_le_utc)}
                      {m.archive ? ' · Archivé' : ''}
                    </p>
                  </div>
                  <div class="doc-modele-actions">
                    <button
                      disabled={occupe}
                      onClick={() =>
                        agir(async () => {
                          const v = (await api(`/modeles/${m.id}`)) as Version
                          ouvrir(v)
                          await generer(v.document, v.id, v.version_id)
                        })
                      }
                    >
                      {modifier && !m.archive
                        ? 'Ouvrir le modèle'
                        : 'Consulter'}
                    </button>
                    {modifier && (
                      <button
                        title={`Dupliquer ${m.nom}`}
                        disabled={occupe}
                        onClick={() =>
                          agir(async () => {
                            const v = await api(
                              `/modeles/${m.id}/dupliquer`,
                              'POST',
                              { version_id: m.version_id },
                            )
                            await charger()
                            ouvrir(v)
                            setMessage('Une copie indépendante est prête.')
                          })
                        }
                      >
                        Dupliquer
                      </button>
                    )}
                  </div>
                </article>
              ))}
          </div>
          {!donnees.modeles.some(
            (m) =>
              m.archive === archives &&
              m.nom.toLocaleLowerCase().includes(recherche.toLocaleLowerCase()),
          ) && (
            <div class="doc-empty">
              <div class="doc-paper-icon" aria-hidden="true">
                ≡
              </div>
              <h3>
                {recherche
                  ? 'Aucun modèle trouvé'
                  : archives
                    ? 'Aucun modèle archivé'
                    : 'Votre premier contrat commence ici'}
              </h3>
              <p>
                {recherche
                  ? 'Essayez un autre nom.'
                  : archives
                    ? 'Les modèles archivés restent disponibles dans les parcours existants.'
                    : 'Un point de départ simple, avec des clauses que vous pouvez adapter et vos coordonnées.'}
              </p>
              {modifier && !archives && !recherche && (
                <button class="doc-primary" onClick={nouveau}>
                  Créer mon premier modèle
                </button>
              )}
            </div>
          )}
        </>
      )}
      {donnees && !edition && onglet === 'identite' && identite && (
        <>
          <div class="doc-heading">
            <div>
              <h2>Une présentation à votre image</h2>
              <p>Cette identité sera proposée pour vos prochains modèles.</p>
            </div>
            {modifier && (
              <button
                class="doc-primary"
                disabled={occupe || !marqueChangee}
                onClick={() =>
                  agir(async () => {
                    await api('/identite', 'PUT', identite)
                    await charger()
                    setMessage(
                      'Identité enregistrée. Les versions de contrats existantes sont conservées.',
                    )
                  })
                }
              >
                {occupe ? 'Enregistrement…' : 'Enregistrer l’identité'}
              </button>
            )}
          </div>
          <div class="doc-brand-grid">
            <div class="doc-fields">
              <fieldset disabled={!modifier || occupe}>
                <legend>Logo et couleur</legend>
                <div class="doc-logo-row">
                  <div class="doc-logo">
                    {identite.logo_jpeg_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${identite.logo_jpeg_base64}`}
                        alt="Votre logo"
                      />
                    ) : (
                      <span>Aa</span>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => importLogo.current?.click()}
                    >
                      {identite.logo_jpeg_base64
                        ? 'Changer le logo'
                        : 'Importer un logo'}
                    </button>
                    <input
                      hidden
                      ref={importLogo}
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => logo(e.currentTarget.files?.[0])}
                    />
                    <p class="doc-help">PNG ou JPEG · 750 Ko maximum</p>
                    {identite.logo_jpeg_base64 && (
                      <button
                        class="doc-link"
                        onClick={() =>
                          setIdentite({
                            ...identite,
                            logo_jpeg_base64: undefined,
                          })
                        }
                      >
                        Retirer le logo
                      </button>
                    )}
                  </div>
                </div>
                <label>
                  Couleur principale
                  <div class="doc-color">
                    <input
                      type="color"
                      aria-label="Choisir la couleur"
                      value={
                        /^#[0-9a-f]{6}$/i.test(identite.couleur)
                          ? identite.couleur
                          : '#1d4ed8'
                      }
                      onInput={(e) =>
                        setIdentite({
                          ...identite,
                          couleur: e.currentTarget.value,
                        })
                      }
                    />
                    <input
                      value={identite.couleur}
                      maxLength={7}
                      aria-label="Code de la couleur"
                      onInput={(e) =>
                        setIdentite({
                          ...identite,
                          couleur: e.currentTarget.value,
                        })
                      }
                    />
                  </div>
                </label>
              </fieldset>
              <fieldset disabled={!modifier || occupe}>
                <legend>Coordonnées du document</legend>
                <label>
                  Nom de l’organisation
                  <input
                    value={identite.nom}
                    maxLength={160}
                    onInput={(e) =>
                      setIdentite({ ...identite, nom: e.currentTarget.value })
                    }
                  />
                </label>
                <label>
                  Adresse et coordonnées
                  <textarea
                    rows={5}
                    value={identite.coordonnees}
                    maxLength={1200}
                    onInput={(e) =>
                      setIdentite({
                        ...identite,
                        coordonnees: e.currentTarget.value,
                      })
                    }
                  />
                </label>
                <label>
                  Pied de page
                  <input
                    value={identite.pied_page}
                    maxLength={240}
                    onInput={(e) =>
                      setIdentite({
                        ...identite,
                        pied_page: e.currentTarget.value,
                      })
                    }
                  />
                </label>
              </fieldset>
            </div>
            <div
              class="doc-brand-preview"
              style={{
                borderTopColor: /^#[0-9a-f]{6}$/i.test(identite.couleur)
                  ? identite.couleur
                  : '#1d4ed8',
              }}
            >
              <div class="doc-brand-letterhead">
                {identite.logo_jpeg_base64 && (
                  <img
                    src={`data:image/jpeg;base64,${identite.logo_jpeg_base64}`}
                    alt=""
                  />
                )}
                <div>
                  <strong>{identite.nom || 'Votre organisation'}</strong>
                  <p>{identite.coordonnees || 'Vos coordonnées'}</p>
                </div>
              </div>
              <h3>Conditions de réservation</h3>
              <p class="doc-placeholder-line" />
              <p class="doc-placeholder-line" />
              <p class="doc-placeholder-line doc-short" />
              <h4
                style={{
                  color: /^#[0-9a-f]{6}$/i.test(identite.couleur)
                    ? identite.couleur
                    : '#1d4ed8',
                }}
              >
                1. Utilisation des lieux
              </h4>
              <p class="doc-placeholder-line" />
              <p class="doc-placeholder-line" />
              <p class="doc-placeholder-line doc-short" />
              <footer>{identite.pied_page || identite.nom}</footer>
            </div>
          </div>
        </>
      )}
      {donnees && !edition && onglet === 'signes' && (
        <>
          <div class="doc-heading">
            <div>
              <h2>Les exemplaires acceptés et signés</h2>
              <p>
                Chaque PDF conserve le texte accepté et la preuve de signature.
              </p>
            </div>
          </div>
          {donnees.signes.length ? (
            <div class="doc-signes">
              {donnees.signes.map((s) => (
                <article key={s.id}>
                  <div>
                    <strong>{s.nom_tape}</strong>
                    <p>Signé le {date(s.signe_le_utc)}</p>
                  </div>
                  <a
                    class="doc-download"
                    href={`${baseUrl}/api/admin/demandes/${encodeURIComponent(s.demande_id)}/contrat.pdf`}
                    target="_blank"
                    rel="noopener"
                  >
                    Ouvrir le PDF ↗
                  </a>
                </article>
              ))}
            </div>
          ) : (
            <div class="doc-empty">
              <h3>Aucun document signé pour le moment</h3>
              <p>
                Les exemplaires apparaîtront ici lorsqu’une personne aura
                terminé une étape Signature.
              </p>
            </div>
          )}
        </>
      )}
      {edition && donnees && (
        <>
          <div class="doc-editor-heading">
            <button class="doc-link" disabled={occupe} onClick={quitter}>
              ← Tous les modèles
            </button>
            <div class="doc-editor-actions">
              {badge(
                versionLue
                  ? `Historique · v${edition.numero}`
                  : edition.id
                    ? `Version ${edition.numero}`
                    : 'Nouveau modèle',
              )}
              {change && !versionLue && (
                <span class="doc-unsaved">Non enregistré</span>
              )}
              {editPossible && (
                <button
                  class="doc-primary"
                  disabled={occupe || !change}
                  onClick={sauvegarder}
                >
                  {occupe ? 'Enregistrement…' : 'Enregistrer le modèle'}
                </button>
              )}
            </div>
          </div>
          {versionLue && (
            <div class="doc-notice">
              Vous consultez une version conservée.
              <button
                onClick={() =>
                  agir(async () => {
                    const v = await api(`/modeles/${edition.id}`)
                    ouvrir(v)
                    await generer(v.document, v.id, v.version_id)
                  })
                }
              >
                Revenir à la version actuelle
              </button>
            </div>
          )}
          {edition.archive && (
            <div class="doc-notice">
              Ce modèle est archivé. Les parcours qui l’utilisent conservent
              leur contrat.
              {modifier && (
                <button
                  onClick={() =>
                    agir(async () => {
                      const v = await api(
                        `/modeles/${edition.id}/restaurer`,
                        'POST',
                        { version_id: edition.version_id },
                      )
                      await charger()
                      ouvrir(v)
                    })
                  }
                >
                  Restaurer le modèle
                </button>
              )}
            </div>
          )}
          <div class="doc-editor-grid">
            <div class="doc-fields">
              <fieldset disabled={!editPossible || occupe}>
                <legend>Le document</legend>
                <label>
                  Nom du modèle{' '}
                  <span class="doc-help">Visible par votre équipe</span>
                  <input
                    value={edition.document.nom}
                    maxLength={100}
                    onInput={(e) => patch({ nom: e.currentTarget.value })}
                  />
                </label>
                <label>
                  Titre du contrat
                  <input
                    value={edition.document.titre}
                    maxLength={160}
                    onFocus={(e) => {
                      cible.current = {
                        element: e.currentTarget,
                        champ: 'titre',
                      }
                    }}
                    onInput={(e) => patch({ titre: e.currentTarget.value })}
                  />
                </label>
                {champ(
                  'Introduction',
                  'introduction',
                  edition.document.introduction,
                )}
              </fieldset>
              {editPossible && (
                <div class="doc-variables">
                  <label>
                    Ajouter un renseignement automatique
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.currentTarget.value)
                          inserer(e.currentTarget.value)
                        e.currentTarget.value = ''
                      }}
                    >
                      <option value="">Choisir une variable…</option>
                      {donnees.variables.map((v) => (
                        <option value={v.cle}>{v.libelle}</option>
                      ))}
                    </select>
                  </label>
                  <p>
                    Placez le curseur dans un texte, puis choisissez une
                    variable. Les dates et montants nécessitent les étapes
                    correspondantes dans le parcours.
                  </p>
                  <p>
                    Pour reprendre une réponse personnalisée, copiez sa
                    référence dans les réglages avancés du champ, section
                    Formulaires, puis collez-la ici. Elle sera remplie lorsque
                    ce formulaire précède le contrat.
                  </p>
                </div>
              )}
              <div class="doc-clauses-heading">
                <h3>Clauses</h3>
                <span>{edition.document.clauses.length} clauses</span>
              </div>
              {edition.document.clauses.map((c, i) => (
                <fieldset
                  class="doc-clause"
                  key={c.id}
                  disabled={!editPossible || occupe}
                >
                  <legend>Clause {i + 1}</legend>
                  <div class="doc-clause-tools">
                    <button
                      type="button"
                      aria-label={`Monter la clause ${i + 1}`}
                      disabled={!i}
                      onClick={() => deplacer(c.id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Descendre la clause ${i + 1}`}
                      disabled={i === edition.document.clauses.length - 1}
                      onClick={() => deplacer(c.id, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label={`Dupliquer la clause ${i + 1}`}
                      disabled={edition.document.clauses.length >= 40}
                      onClick={() => {
                        const clauses = [...edition.document.clauses]
                        clauses.splice(i + 1, 0, { ...c, id: uid() })
                        patch({ clauses })
                      }}
                    >
                      Dupliquer
                    </button>
                    <button
                      type="button"
                      class="doc-danger"
                      aria-label={`Supprimer la clause ${i + 1}`}
                      disabled={edition.document.clauses.length < 2}
                      onClick={() =>
                        patch({
                          clauses: edition.document.clauses.filter(
                            (cl) => cl.id !== c.id,
                          ),
                        })
                      }
                    >
                      Retirer
                    </button>
                  </div>
                  <label>
                    Titre de la clause
                    <input
                      value={c.titre}
                      maxLength={140}
                      onFocus={(e) => {
                        cible.current = {
                          element: e.currentTarget,
                          champ: 'titre',
                          clause: c.id,
                        }
                      }}
                      onInput={(e) =>
                        patchClause(c.id, { titre: e.currentTarget.value })
                      }
                    />
                  </label>
                  <label>
                    Contenu
                    <textarea
                      rows={5}
                      value={c.texte}
                      maxLength={15000}
                      onFocus={(e) => {
                        cible.current = {
                          element: e.currentTarget,
                          champ: 'texte',
                          clause: c.id,
                        }
                      }}
                      onInput={(e) =>
                        patchClause(c.id, { texte: e.currentTarget.value })
                      }
                    />
                  </label>
                </fieldset>
              ))}
              {editPossible && (
                <button
                  class="doc-add"
                  disabled={edition.document.clauses.length >= 40}
                  onClick={() =>
                    patch({
                      clauses: [
                        ...edition.document.clauses,
                        { id: uid(), titre: 'Nouvelle clause', texte: '' },
                      ],
                    })
                  }
                >
                  + Ajouter une clause
                </button>
              )}
              <fieldset disabled={!editPossible || occupe}>
                <legend>Pour terminer</legend>
                {champ('Conclusion', 'conclusion', edition.document.conclusion)}
              </fieldset>
              <div class="doc-version-panel">
                <h3>Présentation et versions</h3>
                <p>
                  {edition.document.identite.nom} · couleur{' '}
                  <span
                    class="doc-color-dot"
                    style={{ background: edition.document.identite.couleur }}
                  />{' '}
                  {edition.document.identite.couleur}
                </p>
                <p class="doc-help">
                  Chaque version conserve son logo, ses couleurs et ses
                  coordonnées.
                </p>
                {editPossible && (
                  <button
                    onClick={() => patch({ identite: copie(donnees.identite) })}
                  >
                    Appliquer l’identité visuelle actuelle
                  </button>
                )}
                {edition.id && (
                  <button
                    onClick={() =>
                      agir(async () => {
                        const h = await api(`/modeles/${edition.id}/versions`)
                        setHistorique(h.versions)
                      })
                    }
                  >
                    Voir les versions
                  </button>
                )}
                {historique && (
                  <div class="doc-history">
                    {historique.map((h) => (
                      <button
                        onClick={() =>
                          agir(async () => {
                            if (
                              change &&
                              !window.confirm(
                                'Consulter cette version et abandonner les modifications non enregistrées ?',
                              )
                            )
                              return
                            const v = (await api(
                              `/modeles/${edition.id}?version_id=${encodeURIComponent(h.version_id)}`,
                            )) as Version
                            ouvrir(v)
                            setVersionLue(h.version_id)
                            await generer(v.document, v.id, v.version_id)
                          })
                        }
                      >
                        Version {h.numero} · {date(h.cree_le_utc)}
                      </button>
                    ))}
                  </div>
                )}
                {edition.id && editPossible && (
                  <button
                    class="doc-link doc-danger"
                    onClick={() =>
                      agir(async () => {
                        if (
                          !window.confirm(
                            'Archiver ce modèle ? Les contrats déjà utilisés dans les parcours seront conservés.',
                          )
                        )
                          return
                        await api(`/modeles/${edition.id}/archiver`, 'POST', {
                          version_id: edition.version_id,
                        })
                        setEdition(null)
                        await charger()
                        setMessage('Le modèle a été archivé.')
                      })
                    }
                  >
                    Archiver ce modèle
                  </button>
                )}
              </div>
            </div>
            <aside class="doc-preview">
              <div class="doc-preview-toolbar">
                <div>
                  <strong>Aperçu du contrat</strong>
                  <p>Données fictives · PDF au format lettre</p>
                </div>
                <div>
                  <button
                    disabled={apercuOccupe || occupe}
                    onClick={() => generer()}
                  >
                    {apercuOccupe
                      ? 'Préparation…'
                      : apercu
                        ? 'Actualiser'
                        : 'Voir l’aperçu'}
                  </button>
                  <button
                    disabled={
                      !apercu ||
                      apercuOccupe ||
                      apercu.source !== JSON.stringify(edition.document)
                    }
                    onClick={telecharger}
                  >
                    PDF ↓
                  </button>
                </div>
              </div>
              {apercu && apercu.source !== JSON.stringify(edition.document) && (
                <p class="doc-preview-stale">
                  Le texte a changé. Actualisez l’aperçu avant de télécharger le
                  PDF.
                </p>
              )}
              {apercuErreur && (
                <p class="doc-alert" role="alert">
                  {apercuErreur}
                </p>
              )}
              {apercu ? (
                <iframe
                  title="Aperçu du contrat avec des données fictives"
                  sandbox=""
                  srcDoc={apercu.html}
                />
              ) : (
                <div class="doc-preview-empty">
                  <div class="doc-paper-icon" aria-hidden="true">
                    ≡
                  </div>
                  <h3>Votre contrat prend forme</h3>
                  <p>
                    Rédigez vos conditions puis affichez le document tel qu’il
                    sera présenté.
                  </p>
                  <button disabled={apercuOccupe} onClick={() => generer()}>
                    Afficher l’aperçu
                  </button>
                </div>
              )}
              <p class="doc-help">
                Adaptez les conditions à vos pratiques avant d’utiliser ce
                modèle dans un parcours.
              </p>
            </aside>
          </div>
        </>
      )}
    </section>
  )
}

export const stylesDocuments = `
.doc-pane{color:#2a1632;min-width:0}.doc-pane button,.doc-download{font:inherit;font-size:13px;font-weight:550;border:1px solid #eaddf2;background:#fff;border-radius:9px;padding:9px 13px;cursor:pointer;text-decoration:none;color:#2a1632;white-space:normal}.doc-pane button:hover,.doc-download:hover{background:#f9f5fb;border-color:#c3c3ee}.doc-pane button:disabled{opacity:.45;cursor:default}.doc-pane button:focus-visible,.doc-pane input:focus-visible,.doc-pane textarea:focus-visible,.doc-pane select:focus-visible,.doc-pane a:focus-visible{outline:3px solid #c2c3ed;outline-offset:2px}.doc-pane .doc-primary{background:#391d44;border-color:#391d44;color:white}.doc-pane .doc-primary:hover{background:#391d44}.doc-tabs{display:flex;gap:5px;border-bottom:1px solid #f0e8f3;padding-bottom:14px;margin-bottom:26px;flex-wrap:wrap}.doc-tabs button{border:0;background:transparent;color:#7d6985}.doc-tabs button[aria-selected=true]{background:#f5eff9;color:#391d44}.doc-heading{display:flex;align-items:center;justify-content:space-between;gap:20px;margin-bottom:25px}.doc-pane h2{font-size:21px;line-height:1.35;letter-spacing:-.45px;margin:0 0 6px}.doc-pane h3{font-size:16px;line-height:1.4;margin:0}.doc-pane p{font-size:13px;line-height:1.6;margin:5px 0;color:#7d6985}.doc-heading>button{flex:none}.doc-search{display:flex;align-items:center;gap:24px;margin-bottom:20px}.doc-search>input{max-width:420px}.doc-search label{display:flex;align-items:center;gap:8px;white-space:nowrap;font-size:13px}.doc-pane input:not([type=checkbox]):not([type=color]):not([type=file]),.doc-pane textarea,.doc-pane select{box-sizing:border-box;width:100%;min-width:0;border:1px solid #e8daf1;border-radius:9px;padding:10px 12px;font:inherit;font-size:14px;color:#2a1632;background:white}.doc-pane input[type=checkbox]{appearance:auto;width:16px;height:16px;padding:0;margin:0;accent-color:#391d44}.doc-pane textarea{line-height:1.6;resize:vertical;min-height:82px}.doc-pane input[hidden]{display:none}.doc-modeles{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px}.doc-modele{padding:22px;background:#fff;border:1px solid #ece2f0;border-radius:15px;display:flex;flex-direction:column;align-items:flex-start;gap:17px;min-width:0}.doc-modele h3{overflow-wrap:anywhere}.doc-paper-icon{font-size:29px;font-weight:400;line-height:1;display:grid;place-items:center;background:#f7f2fa;color:#391d44;border-radius:12px;width:48px;height:54px}.doc-modele-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:auto}.doc-modele-actions button:first-child{color:#391d44;background:#faf8fc;border-color:#ece0f3}.doc-empty{text-align:center;padding:65px 24px;background:#fff;border:1px dashed #ebdef2;border-radius:16px}.doc-empty .doc-paper-icon{margin:0 auto 18px}.doc-empty p{max-width:410px;margin:10px auto 22px}.doc-alert,.doc-success,.doc-notice{padding:13px 16px;border-radius:10px;margin:0 0 18px;font-size:13px;line-height:1.6;overflow-wrap:anywhere}.doc-alert{color:#9b2929;background:#fff1f1;border:1px solid #f0d0d0}.doc-success{color:#19603e;background:#edfaf3;border:1px solid #ccead9}.doc-notice{background:#f7f2fa;color:#391d44}.doc-alert button,.doc-notice button{margin-left:12px}.doc-editor-heading{position:sticky;top:64px;z-index:3;background:#f8f4f9;padding:12px 0;display:flex;align-items:center;justify-content:space-between;gap:15px;margin-bottom:22px;flex-wrap:wrap}.doc-pane .doc-link{border:0;background:transparent;padding:8px 0;color:#391d44}.doc-editor-actions{display:flex;align-items:center;gap:12px;flex-wrap:wrap}.doc-badge{display:inline-block;font-size:11px;font-weight:600;border-radius:99px;background:#f5f0f9;padding:5px 9px;color:#6a5971}.doc-unsaved{font-size:11px;color:#99650b}.doc-editor-grid{display:grid;grid-template-columns:minmax(310px,.9fr) minmax(380px,1.1fr);gap:25px;align-items:start}.doc-fields{min-width:0;display:grid;gap:18px}.doc-fields fieldset{min-width:0;margin:0;padding:18px 20px 20px;border:1px solid #ece2f0;border-radius:13px;background:#fff}.doc-fields legend{font-size:13px;font-weight:650;padding:0 5px;color:#5c4d62}.doc-fields label{display:grid;gap:7px;font-size:12px;font-weight:600;color:#615168;margin:0 0 15px}.doc-fields label:last-child{margin-bottom:0}.doc-fields .doc-help{font-weight:400}.doc-pane .doc-help{font-size:11px;color:#6b5a70;line-height:1.5}.doc-variables{background:#f8f4fb;border-radius:11px;padding:16px}.doc-variables label{display:grid;gap:9px;font-size:12px;font-weight:600;color:#391d44}.doc-variables p{font-size:11px;margin-top:10px}.doc-clauses-heading{display:flex;justify-content:space-between;align-items:center;margin:4px 0 -8px}.doc-clauses-heading span{font-size:11px;color:#6b5a70}.doc-clause-tools{display:flex;justify-content:flex-end;gap:5px;margin:-4px 0 16px;flex-wrap:wrap}.doc-clause-tools button{padding:5px 9px;font-size:11px}.doc-pane .doc-danger{color:#ad3d3d}.doc-pane .doc-add{border-style:dashed;border-color:#c5c6ee;background:#fcfafd;color:#391d44;padding:13px}.doc-version-panel{padding:20px;border:1px solid #ede3f1;border-radius:13px;background:#fcfafd}.doc-version-panel h3{font-size:13px;margin-bottom:10px}.doc-version-panel button{margin:10px 8px 0 0;font-size:12px}.doc-color-dot{display:inline-block;width:9px;height:9px;border-radius:50%;vertical-align:middle}.doc-history{display:flex;flex-direction:column;align-items:flex-start;border-top:1px solid #ece2f0;margin-top:13px;padding-top:5px}.doc-preview{position:sticky;top:80px;min-width:0;border:1px solid #ece2f0;background:#f8f5fa;border-radius:14px;overflow:hidden}.doc-preview-toolbar{padding:17px;display:flex;align-items:center;justify-content:space-between;gap:12px;background:white;border-bottom:1px solid #ece2f0}.doc-preview-toolbar strong{font-size:13px}.doc-preview-toolbar p{font-size:10px}.doc-preview-toolbar>div:last-child{display:flex;gap:6px}.doc-preview-toolbar button{font-size:11px;padding:8px 10px}.doc-preview iframe{display:block;width:100%;height:700px;max-height:80vh;border:0;background:#f8f4f9}.doc-preview>.doc-help{padding:7px 17px 12px;margin:0}.doc-preview-empty{display:flex;align-items:center;flex-direction:column;justify-content:center;text-align:center;min-height:430px;padding:30px}.doc-preview-empty h3{margin-top:20px}.doc-preview-empty p{max-width:260px;margin:9px 0 20px}.doc-preview .doc-preview-stale{padding:9px 17px;background:#fff6e4;color:#88601d;font-size:11px;margin:0}.doc-preview .doc-alert{margin:12px}.doc-brand-grid{display:grid;grid-template-columns:minmax(280px,.85fr) minmax(320px,1.15fr);gap:35px;align-items:start}.doc-logo-row{display:flex;gap:20px;align-items:center;margin-bottom:22px}.doc-logo{width:85px;height:85px;flex:none;border:1px solid #eee5f1;border-radius:12px;display:grid;place-items:center;background:#fcfafd}.doc-logo img{max-width:74px;max-height:74px;object-fit:contain}.doc-logo>span{font-size:26px;color:#6b5a70}.doc-color{display:flex;gap:12px;align-items:center}.doc-pane input[type=color]{appearance:auto;padding:3px;width:46px;height:40px;flex:none;border:1px solid #e8daf1;background:white;border-radius:7px}.doc-color input:last-child{max-width:150px}.doc-brand-preview{background:white;border:1px solid #ede4f1;border-top:6px solid #391d44;border-radius:3px;padding:36px;box-shadow:0 8px 28px #391d4410;min-height:530px;box-sizing:border-box}.doc-brand-letterhead{display:flex;align-items:center;gap:20px;padding-bottom:24px;border-bottom:1px solid #f0e8f3;margin-bottom:30px;overflow-wrap:anywhere}.doc-brand-letterhead img{width:86px;max-height:70px;object-fit:contain}.doc-brand-letterhead strong{font-size:14px}.doc-brand-letterhead p{white-space:pre-wrap;font-size:11px}.doc-brand-preview h3{font-size:21px;margin-bottom:25px}.doc-brand-preview h4{margin-top:35px;font-size:14px}.doc-brand-preview .doc-placeholder-line{height:7px;background:#f1eaf4;border-radius:3px;margin:13px 0}.doc-brand-preview .doc-short{width:65%}.doc-brand-preview footer{border-top:1px solid #ede4f1;margin-top:100px;padding-top:14px;color:#6b5a70;font-size:10px;overflow-wrap:anywhere}.doc-signes{background:white;border:1px solid #ede3f1;border-radius:14px;overflow:hidden}.doc-signes article{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;gap:15px;border-bottom:1px solid #f4eef6}.doc-signes article:last-child{border:0}.doc-signes strong{font-size:14px}.doc-signes p{font-size:11px}.doc-download{color:#391d44;font-size:12px;flex:none}
@media(max-width:1180px){.doc-editor-grid{grid-template-columns:minmax(300px,1fr) minmax(310px,1fr);gap:18px}.doc-preview-toolbar{flex-wrap:wrap}.doc-brand-grid{gap:20px}.doc-brand-preview{padding:25px}}@media(max-width:900px){.doc-editor-grid,.doc-brand-grid{grid-template-columns:1fr}.doc-preview{position:static}.doc-preview iframe{height:680px}.doc-brand-preview{max-width:600px;width:100%;margin:auto}.doc-editor-actions{gap:8px}}@media(max-width:560px){.doc-editor-heading{top:60px}.doc-heading{align-items:flex-start;flex-direction:column;gap:15px}.doc-heading h2{font-size:19px}.doc-search{align-items:stretch;flex-direction:column;gap:12px}.doc-modeles{grid-template-columns:1fr}.doc-fields fieldset{padding:16px 14px}.doc-editor-heading{align-items:flex-start}.doc-editor-actions{width:100%}.doc-editor-actions .doc-primary{margin-left:auto}.doc-logo-row{gap:13px}.doc-brand-preview{padding:22px;min-height:460px}.doc-brand-letterhead{gap:13px}.doc-brand-letterhead img{width:62px}.doc-signes article{padding:16px;align-items:flex-start;flex-direction:column}.doc-tabs{gap:0}.doc-tabs button{padding:9px 10px;font-size:12px}.doc-empty{padding:45px 20px}}
`
